import axios, { AxiosInstance, AxiosRequestConfig } from 'axios';
import {
  ClientSensitiveField,
  IApplicationModel,
  IClientCardModel,
  IClientCommentModel,
  IContractorModel,
  IDefActPositionModel,
  IDefectModel,
  IEventListItemModel,
  IExecutorModel,
  IFileInfo,
  IKSKDebtModel,
  IOktellOperator,
  IOwnerModel,
  IPositionModel,
  IRoomWarrantyInfoModel,
  IUserModel,
} from './interfaces';
import { IClientProfileResponse } from 'components/ClientInsightCard/interfaces';
import { isSystemFailure, rememberFailure } from './apiFailures';
import { ensureFreshToken } from './keycloak';

export const SERVICE_API_URL = '/api';
export const PPR_API_URL = '/api-ppr';
export const CRM_REPORT_API_URL = '/api-crm-report';

const instance = (auth: boolean = true, url: string = SERVICE_API_URL): AxiosInstance => {
  const axiosInstance = axios.create({
    baseURL: url,
  });

  if (auth) {
    axiosInstance.interceptors.request.use(
      async (config) => {
        // Токен берём у keycloak, а не из localStorage: там лежит копия, которая обновляется
        // следом за ним и в момент запроса может отставать.
        const token = await ensureFreshToken();
        if (token) config.headers.Authorization = `Bearer ${token}`;
        return config;
      },
      (error) => Promise.reject(error),
    );

    axiosInstance.interceptors.response.use(
      (response) => {
        return response;
      },
      async function (error) {
        const config = error.config as (AxiosRequestConfig & { _retried?: boolean }) | undefined;

        // 401 на токене, который по нашим часам ещё годен: часы разошлись с сервером, либо
        // токен отозвали досрочно. Обновляемся принудительно и повторяем запрос — один раз,
        // чтобы не зациклиться, если дело не в токене. Заголовок проставит интерцептор запроса.
        if (error.response?.status === 401 && config && !config._retried) {
          config._retried = true;

          const token = await ensureFreshToken(true);
          if (token) return axiosInstance(config);
        }

        if (isSystemFailure(error)) rememberFailure(error);

        // Дальше не логаутим: если сессия действительно мертва, keycloak-js уже уводит
        // на страницу входа сам. Прежний безусловный logout выбрасывал пользователя
        // из системы и на разовом сбое сети, теряя всё, что он успел заполнить.
        return Promise.reject(error);
      },
    );
  }

  return axiosInstance;
};

export const getApplicationByNumber = (applicationNumber: string) => {
  return instance()
    .post<IApplicationModel>('/application/v3/loadByApplicationNumber', { applicationNumber })
    .then((response) => response.data);
};

// Полная карточка клиента по телефону (объекты, договоры, арендатор, скидки,
// история NPS, кол-во заявок, контакты). Возвращает null, если клиента нет.
export const getClientCard = (phoneNumber: string) => {
  return instance()
    .post<IClientCardModel | null>('/client-info/card', { phoneNumber })
    .then((response) => response.data);
};

// Полное значение маскированного ПДн (ИИН, телефон супруга/арендатора) карточки клиента.
export const revealClientValue = (phoneNumber: string, field: ClientSensitiveField, roomGuid?: string | null) => {
  return instance()
    .post<{ value: string | null }>('/client-info/reveal', { phoneNumber, field, roomGuid })
    .then((response) => response.data.value);
};

// Комментарии к клиенту в карточке клиента (ключ — номер телефона).
// Грузятся порциями: карточка открывается с двумя комментариями, дальше догружает по десять.
export const getClientComments = (phoneNumber: string, offset: number, limit: number) => {
  return instance()
    .post<{ list: IClientCommentModel[]; totalCount: number }>('/client-info/comment/list', {
      phoneNumber,
      offset,
      limit,
    })
    .then((response) => response.data);
};

export const saveClientComment = (phoneNumber: string, comment: string) => {
  return instance()
    .post<IClientCommentModel>('/client-info/comment/save', { phoneNumber, comment })
    .then((response) => response.data);
};

export const updateClientComment = (id: number, comment: string) => {
  return instance()
    .post<IClientCommentModel>('/client-info/comment/update', { id, comment })
    .then((response) => response.data);
};

export const deleteClientComment = (id: number) => {
  return instance()
    .post<void>('/client-info/comment/delete', { id })
    .then((response) => response.data);
};

// Единый профиль клиента (сборка sauron через LLM): карточки оператора/менеджера + аналитика.
// Ответ долгий — обычно ~8–20 сек, изредка до ~120 сек.
export const getClientProfile = (phoneNumber: string) => {
  return instance()
    .post<IClientProfileResponse>('/ai/client-profile', { phoneNumber })
    .then((response) => response.data);
};

export const getCurrentUser = () => {
  return instance()
    .post('/users/currentUser', {})
    .then((response) => response.data);
};

export const updateFirebaseToken = (token: string) => {
  return instance()
    .post('/users/v2/updateFirebaseTokenWeb', { token })
    .then((response) => response.data);
};

export const getContractors = (realEstateGuid: string) => {
  return instance()
    .post<IContractorModel[]>('/dictionary/contractorsExt', { realEstateGuid })
    .then((response) => response.data);
};

export const getOwner = (roomGuid: string) => {
  return instance()
    .post<IOwnerModel>('/application/getOwner', { roomGuid })
    .then((response) => response.data);
};

export const roomWarrantyInfo = (roomGuid: string) => {
  return instance()
    .post<IRoomWarrantyInfoModel>('/application/roomWarrantyInfo', { roomGuid })
    .then((response) => response.data);
};

export const getKSKDebt = (apartmentGuid: string, kontragentGuid: string) => {
  return instance()
    .post<IKSKDebtModel>('/application/getDebtKSK', { apartmentGuid, kontragentGuid })
    .then((resposne) => resposne.data);
};

export const getRandomExecutor = (realEstateGuid: string, defectTypeId: number) => {
  return instance()
    .get<IExecutorModel>('/executor/randomExecutor', { params: { realEstateGuid, defectTypeId } })
    .then((response) => response.data);
};

export const getOperations = (
  data: {
    applicationNumber?: string;
    areaId?: number;
    serviceOrgGuid?: string;
    realEstateGuid?: string;
    defectTypeId?: number;
  } = {},
) => {
  return instance()
    .post<string[]>('/security/operationsAdmin', data)
    .then((response) => response.data);
};

export const upload = (files: File[]): Promise<string[]> => {
  return new Promise((resolve, reject) => {
    if (files.length > 0) {
      const formData = new FormData();

      files.forEach((file: File) => {
        formData.append('files', file);
      });

      instance()
        .post('/file/upload', formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        })
        .then((response) => response.data)
        .then(resolve)
        .catch(reject);
    } else {
      resolve([]);
    }
  });
};

export const getPositions = () => {
  return instance()
    .post<IPositionModel[]>('/dictionary/positions', {})
    .then((response) => response.data);
};

export const getDefActPositions = () => {
  return instance()
    .post<IDefActPositionModel[]>('/dictionary/defActPositions', {})
    .then((response) => response.data);
};

export const getRealEstateEvents = (realEstateGuid: string, onlyActive: boolean = false) => {
  return instance()
    .post<IEventListItemModel[]>('/event/list', { realEstateGuid, onlyActive })
    .then((response) => response.data);
};

export const checkRealEstateEvents = (realEstateGuid: string) => {
  return instance()
    .get<{ hasEvent: boolean }>('/event/hasEvent', { params: { realEstateGuid } })
    .then((response) => response.data);
};

export const getImagesInfo = (files: string[]) => {
  return instance()
    .post<IFileInfo[]>('/file/getDownloadURL', files)
    .then((response) => response.data);
};

export const getImageInfo = (imageName: string) => {
  return instance()
    .post<
      {
        downloadOptimizedUrl: string;
        downloadThumbnailUrl: string;
        downloadUrl: string;
      }[]
    >('/file/getDownloadURL', [imageName])
    .then((response) => response.data);
};

export const saveChecklist = (realEstateGuid: string, telephoneNumber: string) => {
  return instance()
    .post('/checklist/saveChecklist', { realEstateGuid, telephoneNumber })
    .then((response) => response.data);
};

export const loadOktellCall = () => {
  return instance()
    .post('/octell/v2/loadOktellCall', {})
    .then((response) => response.data);
};

export const loadOperatorContour = () => {
  return instance()
    .post<IOktellOperator>('/octell/v2/operatorContour', {})
    .then((response) => response.data);
};

export const checkUpcomingCallbacks = (callbackTimestamp: string) => {
  return instance()
    .post('/callcenter/callbacks', { callbackTimestamp })
    .then((response) => response.data);
};

export const listPerformance = (search: string) => {
  return instance()
    .post<IUserModel[]>('/users/listPerformance', {
      search,
    })
    .then((response) => response.data);
};

export const getDefectsAll = () => {
  return instance()
    .post<IDefectModel[]>('/dictionary/defects', {})
    .then((response) => response.data);
};

export const listPerformanceConfig = (search: string, config: AxiosRequestConfig = {}) => {
  return instance()
    .post<IUserModel[]>('/users/listPerformance', { search }, config)
    .then((response) => response.data);
};

export const checkInternetConnection = () => {
  return instance().post('/checkConnection/check');
};

export const addPhoneNumber = (phone: string) => {
  return instance()
    .post('/employeeReport/updatePhone', { phone })
    .then((response) => response.data);
};

export const getAgreementFile = () => {
  return instance()
    .get('/users/agreementFile', { responseType: 'blob' })
    .then((response) => response.data);
};

export const acceptAgreement = () => {
  return instance()
    .post('/users/agreementAccepted', {})
    .then((response) => response.data);
};

export default instance;
